import { isReactive, isRef, toRaw } from 'vue'

/**
 * Recursively unwraps Vue 3 reactivity (reactive proxies and refs) into a plain
 * object tree.
 *
 * Vue 3 stores reactive state behind `Proxy` objects, and `structuredClone`
 * throws `DataCloneError` when given a Proxy. Vue's own `toRaw` only unwraps the
 * top level, so nested reactive values still break the clone. This walks the
 * whole structure so the result can be safely passed to `structuredClone`.
 *
 * Only plain objects and arrays are rebuilt; every other value (primitives,
 * `Date`, `Map`, class instances, …) is returned as its raw form so the caller
 * can clone it faithfully.
 */
export function deepToRaw<T>(value: T): T {
  if (isRef(value)) {
    return deepToRaw(value.value) as T
  }

  const raw = isReactive(value) ? toRaw(value) : value

  if (Array.isArray(raw)) {
    return raw.map((item) => deepToRaw(item)) as T
  }

  if (isPlainObject(raw)) {
    const result: Record<string, unknown> = {}
    for (const key of Object.keys(raw)) {
      result[key] = deepToRaw(raw[key])
    }
    return result as T
  }

  return raw
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false
  }
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}
